import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Sparkles, UploadCloud, Loader2, CheckCircle2, AlertTriangle, FileText } from 'lucide-react';
import { Button } from './ui/button';

/**
 * AI-powered file upload for a specific module.
 * Accepts PDF/Excel/CSV/Image, sends to /api/upload/module/{module}/{client_id},
 * and reports auto-imported vs pending-review counts.
 */
export const ModuleUploadZone = ({ module, moduleLabel, accept, clientId, API, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API}/upload/module/${module}/${clientId}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setResult(res.data);
      onUploadComplete && onUploadComplete(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) handleUpload(f);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleUpload(f);
  };

  return (
    <div className="bg-white border border-border rounded-md p-5" data-testid={`module-upload-${module}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Sparkles className="h-4 w-4 text-state-info" />
          <h4 className="text-sm font-semibold text-primary">AI Document Upload — {moduleLabel}</h4>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">
          PDF · Excel · CSV · Image
        </span>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-primary bg-background-secondary' : 'border-border hover:border-primary'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        data-testid={`module-upload-dropzone-${module}`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept || ".pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg"}
          onChange={onFileChange}
          className="hidden"
          data-testid={`module-upload-input-${module}`}
        />
        {uploading ? (
          <div className="flex flex-col items-center space-y-2 text-text-secondary">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <p className="text-sm font-medium">AI is extracting data...</p>
            <p className="text-xs text-text-muted">This usually takes 5-15 seconds depending on the file.</p>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <UploadCloud className="h-7 w-7 text-text-muted" />
            <p className="text-sm text-text-secondary">
              <span className="font-semibold text-primary">Click to upload</span> or drag a file
            </p>
            <p className="text-[11px] text-text-muted">
              AI auto-imports high-confidence rows, sends others to review queue
            </p>
          </div>
        )}
      </div>

      {result && (
        <div className="mt-4 bg-background-secondary rounded-md p-3 text-sm" data-testid={`module-upload-result-${module}`}>
          <div className="flex items-center space-x-2 mb-2">
            <CheckCircle2 className="h-4 w-4 text-state-success" />
            {result.transactions_written > 0 ? (
              <span className="font-semibold text-primary">
                Captured {result.transactions_written} bill-level rows
                <span className="text-text-muted font-normal"> · {result.rows_extracted} daily rollup{result.rows_extracted === 1 ? '' : 's'}</span>
              </span>
            ) : (
              <span className="font-semibold text-primary">Extracted {result.rows_extracted} rows</span>
            )}
            <span className="text-[10px] uppercase tracking-wider text-text-muted">
              {result.model_used}
            </span>
            {result.escalated > 0 && (
              <span className="text-[10px] text-state-warning bg-yellow-50 px-1.5 py-0.5 rounded">
                Escalated to Pro
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Stat
              label={result.transactions_written > 0 ? "Bills captured" : "Auto-imported"}
              value={result.transactions_written > 0 ? result.transactions_written : result.auto_imported}
              color="text-state-success"
              testid={`stat-imported-${module}`}
            />
            <Stat label="Needs review" value={result.pending_review} color="text-state-warning" testid={`stat-pending-${module}`} />
            <Stat label="Overall conf." value={`${result.overall_confidence || 0}%`} color="text-primary" testid={`stat-conf-${module}`} />
          </div>
          {result.data_quality && (result.data_quality.arithmetic > 0 || result.data_quality.imputed > 0 || result.data_quality.ai_repaired > 0 || result.data_quality.remaining_suspicious > 0) && (
            <div className="mt-3 bg-blue-50 border border-state-info/30 rounded p-2" data-testid={`module-upload-dq-${module}`}>
              <p className="text-[11px] font-semibold text-state-info uppercase tracking-wider mb-1">
                Data quality auto-repair
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1 text-[11px]">
                <DQStat label="Arithmetic fixes" value={result.data_quality.arithmetic} testid={`dq-arith-${module}`} />
                <DQStat label="Imputed from siblings" value={result.data_quality.imputed} testid={`dq-imputed-${module}`} />
                <DQStat label="AI micro-repaired" value={result.data_quality.ai_repaired} testid={`dq-ai-${module}`} />
                <DQStat label="Still unresolved" value={result.data_quality.remaining_suspicious}
                  color={result.data_quality.remaining_suspicious > 0 ? 'text-state-warning' : 'text-state-success'}
                  testid={`dq-remaining-${module}`} />
              </div>
            </div>
          )}
          {result.issues_detected && result.issues_detected.length > 0 && (
            <div className="mt-3 bg-yellow-50 border border-state-warning/30 rounded p-2" data-testid={`module-upload-issues-${module}`}>
              <p className="text-[11px] font-semibold text-state-warning uppercase tracking-wider mb-1">
                AI noticed in this file
              </p>
              <ul className="text-[11px] text-text-secondary space-y-0.5 list-disc list-inside">
                {result.issues_detected.slice(0, 6).map((iss, i) => (
                  <li key={i}>{iss}</li>
                ))}
              </ul>
            </div>
          )}
          {result.notes && (
            <p className="text-[11px] text-text-muted mt-2">{result.notes}</p>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-3 text-sm flex items-start space-x-2">
          <AlertTriangle className="h-4 w-4 text-state-error flex-shrink-0 mt-0.5" />
          <span className="text-state-error">{error}</span>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, color, testid }) => (
  <div className="bg-white rounded p-2 border border-border" data-testid={testid}>
    <p className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</p>
    <p className={`text-lg font-bold ${color}`}>{value}</p>
  </div>
);

const DQStat = ({ label, value, color = 'text-state-info', testid }) => (
  <div className="bg-white rounded p-1.5 border border-border" data-testid={testid}>
    <p className="text-[9px] uppercase tracking-wider text-text-muted font-semibold leading-tight">{label}</p>
    <p className={`text-sm font-bold ${color}`}>{value}</p>
  </div>
);

export default ModuleUploadZone;
